import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/lib/api';

export type DashboardData = {
  contracts: { active: number; completed: number };
  apartments: Record<string, number>;
  reservationsActive: number;
  paymentsToday: { count: number; total: string };
  overdue: { rows: number; outstanding: string };
};

export type FunnelData = {
  stages: { key: string; name: string; count: number }[];
};

export type CashflowBucket = { month: string; expectedDue: string; projected: string };
export type AgingBucket = { range: string; schedules: number; principal: string };
export type LeaderboardRow = { userId: string; fullName: string; customers: number; contracts: number };

export function useDashboard() {
  return useQuery({
    queryKey: ['report-dashboard'],
    queryFn: async () => {
      const res = await api.get<{ data: DashboardData }>('/reports/dashboard');
      return res.data.data;
    },
  });
}

export function useSalesFunnel(params?: { projectId?: string; from?: string; to?: string }) {
  return useQuery({
    queryKey: ['report-funnel', params],
    queryFn: async () => {
      const res = await api.get<{ data: FunnelData }>('/reports/sales-funnel', { params });
      return res.data.data;
    },
  });
}

export function useCashflow(months: number = 12) {
  return useQuery({
    queryKey: ['report-cashflow', months],
    queryFn: async () => {
      const res = await api.get<{ data: { buckets: CashflowBucket[] } }>('/reports/cashflow', { params: { months } });
      return res.data.data.buckets;
    },
  });
}

export function useOverdueAging() {
  return useQuery({
    queryKey: ['report-aging'],
    queryFn: async () => {
      const res = await api.get<{ data: { buckets: AgingBucket[] } }>('/reports/overdue-aging');
      return res.data.data.buckets;
    },
  });
}

export function useAgentLeaderboard() {
  return useQuery({
    queryKey: ['report-leaderboard'],
    queryFn: async () => {
      const res = await api.get<{ data: { rows: LeaderboardRow[] } }>('/reports/agent-leaderboard');
      return res.data.data.rows;
    },
  });
}

export type ProfitabilityRow = {
  projectId: string;
  name: string;
  code: string;
  contracts: number;
  contractedTotal: string;
  collectedTotal: string;
  collectionRatePercent: number;
  overdueOutstanding: string;
};

export function useProjectProfitability() {
  return useQuery({
    queryKey: ['report-profitability'],
    queryFn: async () => {
      const res = await api.get<{ data: { rows: ProfitabilityRow[] } }>('/reports/project-profitability');
      return res.data.data.rows;
    },
  });
}

export type TimelineItem = { kind: string; ts: string; payload: Record<string, unknown> };

export function useCustomerTimeline(customerId: string | undefined, limit = 100) {
  return useQuery({
    queryKey: ['customer-timeline', customerId, limit],
    enabled: !!customerId,
    queryFn: async () => {
      const res = await api.get<{ data: { items: TimelineItem[] } }>(
        `/reports/customer-timeline/${customerId}`,
        { params: { limit } },
      );
      return res.data.data.items;
    },
  });
}

export type CustomerBalance = {
  contracts: Array<{ id: string; contractNumber: string; status: string; totalAmount: string }>;
  totals: {
    contracted: string;
    paid: string;
    outstanding: string;
    lateFees: string;
    overdueRows: number;
  };
};

export function useCustomerBalance(customerId: string | undefined) {
  return useQuery({
    queryKey: ['customer-balance', customerId],
    enabled: !!customerId,
    queryFn: async () => {
      const res = await api.get<{ data: CustomerBalance }>(`/reports/customer-balance/${customerId}`);
      return res.data.data;
    },
  });
}
